/**
 * AboutDialog - Displays app info, open-source attribution, and sponsor link.
 *
 * Opened from: Help tab "About" button, or clickable version in StatusBar.
 */

import { memo, type ReactNode } from "react";

import {
  GithubLogo,
  Globe,
  Heart,
  Info,
  ArrowSquareOut,
} from "@phosphor-icons/react";

import OwnChartLogo from "@/assets/logo.svg?react";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/common/Button";
import { useUIStore } from "@/store/slices/uiSlice";
import { APP_CONFIG } from "@/config/appConfig";

interface ExternalLinkProps {
  href: string;
  icon: ReactNode;
  label: string;
  sublabel?: string;
}

/**
 * Returns a safe href for use in an anchor tag.
 * Only http and https protocols are permitted — all others are replaced with
 * a safe fallback to prevent javascript: or data: URI injection.
 *
 * In development (`import.meta.env.DEV === true`), a console warning is
 * emitted when the fallback is triggered so that misconfigured URLs in
 * APP_CONFIG are caught early. The warning is omitted in production and in
 * test environments unless the test runner sets `import.meta.env.DEV = true`.
 *
 * Exported for unit testing.
 */
export function sanitizeHref(href: string): string {
  if (href.startsWith("https://") || href.startsWith("http://")) {
    return href;
  }
  if (import.meta.env.DEV) {
    console.warn(
      `[AboutDialog] sanitizeHref: href "${href}" does not start with http(s)://. ` +
        "Falling back to '#'. Check APP_CONFIG for misconfigured URLs."
    );
  }
  return "#";
}

/** Link row used within AboutDialog to display icon + label + optional sublabel. */
const ExternalLink = memo(function ExternalLink({
  href,
  icon,
  label,
  sublabel,
}: ExternalLinkProps): JSX.Element {
  return (
    <a
      href={sanitizeHref(href)}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 group py-1 rounded -mx-1 px-1 hover:bg-slate-50 transition-colors"
    >
      {/* Category icon — decorative; the text label conveys the link purpose */}
      <span
        className="mt-0.5 text-slate-500 group-hover:text-slate-700 flex-shrink-0"
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-slate-700 group-hover:text-slate-900">
          {label}
        </span>
        {sublabel && (
          <span className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-brand-600">
            {sublabel}
            <ArrowSquareOut size={11} weight="regular" aria-hidden="true" />
          </span>
        )}
      </div>
    </a>
  );
});

/** Website URL with the http(s):// prefix stripped for display purposes.
 *  Falls back to the full URL if the replace produces an empty string — this
 *  can only happen when APP_CONFIG.websiteUrl is exactly "https://" or "http://",
 *  which would leave an empty string after the regex removes the protocol. */
const WEBSITE_DISPLAY_URL =
  APP_CONFIG.websiteUrl.replace(/^https?:\/\//, "") || APP_CONFIG.websiteUrl;

export const AboutDialog = memo(function AboutDialog(): JSX.Element | null {
  const { isAboutDialogOpen: isOpen, closeAboutDialog } = useUIStore();

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeAboutDialog}
      title="About"
      icon={<Info size={24} weight="light" className="text-brand-600" />}
      headerStyle="bordered"
      footerStyle="bordered"
      widthClass="max-w-sm"
      contentPadding="px-8 py-6"
      footer={
        <Button variant="secondary" onClick={closeAboutDialog}>
          Close
        </Button>
      }
    >
      {/* Logo + Version */}
      <div className="flex flex-col items-center text-center mb-5">
        <OwnChartLogo
          width={36}
          height={36}
          className="text-brand-600"
          aria-hidden="true"
        />
        <h3 className="mt-3 text-lg font-semibold text-slate-900">
          {APP_CONFIG.name}
        </h3>
        <span className="text-sm text-slate-500">
          {/* __APP_VERSION__ is injected by Vite at build time — see vite.config.ts */}
          Version {__APP_VERSION__}
        </span>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {APP_CONFIG.tagline}
        </p>
      </div>

      <hr className="border-slate-300 mb-4" />

      {/* Open Source + Links */}
      <div className="space-y-3">
        <ExternalLink
          href={APP_CONFIG.githubUrl}
          icon={<GithubLogo size={18} weight="regular" />}
          label={`Open Source (${APP_CONFIG.license} License)`}
          sublabel="GitHub Repository"
        />
        <ExternalLink
          href={APP_CONFIG.websiteUrl}
          icon={<Globe size={18} weight="regular" />}
          label={WEBSITE_DISPLAY_URL}
        />
      </div>

      <hr className="border-slate-300 my-4" />

      {/* Sponsor */}
      <ExternalLink
        href={APP_CONFIG.sponsorUrl}
        icon={<Heart size={18} weight="fill" className="text-red-500" />}
        label="Support this project"
        sublabel="GitHub Sponsors"
      />
    </Modal>
  );
});
